import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Theme } from "@mui/material/styles";
import { useQuery } from "@apollo/client";
import { useAccountContext } from "../context/AccountContext/useAccountContext";
import { useUserProfile } from "../hooks/useUserProfile";
import {
  GET_AVAILABLE_ACCOUNT_CONTEXTS,
  GetAvailableAccountContextsData,
} from "common/graphql/queries/GetAvailableAccountContexts";
import { useAccountContextBannerVisible } from "./useAccountContextBannerVisible";

/** Fixed vertical size of the banner, in pixels. Consumers add this to their content
 * top offset so the banner doesn't overlap the page body. */
export const ACCOUNT_CONTEXT_BANNER_HEIGHT = 24;

/**
 * A thin, full-width banner that sits directly below the NavBar and labels the
 * account context the user is currently viewing (e.g. "Viewing: Philadelphia Bus System").
 *
 * Visibility is delegated entirely to {@link useAccountContextBannerVisible} so
 * the layout's top-padding reservation and this component's render decision
 * share one source of truth — see that hook's docstring for the full predicate.
 *
 * Lives below the NavBar at `top: NAVBAR_HEIGHT` per breakpoint. Does not slide
 * on scroll — it stays permanently visible so the viewing context is never in doubt.
 *
 * **Query reuse:** the same `AvailableAccountContexts` query is fired by
 * {@link AccountContextInitializer} and {@link AccountContextDropdown}, so on a
 * warm cache this read is free. Cold mount falls back to a deduplicated network
 * request via Apollo's default `cache-first` policy.
 */
export const AccountContextBanner: React.FC = () => {
  const { contextId } = useAccountContext();
  const { userProfile } = useUserProfile();
  const { data } = useQuery<GetAvailableAccountContextsData>(GET_AVAILABLE_ACCOUNT_CONTEXTS);
  const visible = useAccountContextBannerVisible();

  const availableContexts = useMemo(() => data?.availableAccountContexts ?? [], [data]);

  // The `null` branches here are unreachable when `visible` is true (the hook
  // proves `userProfile` is set and `activeId` resolves to an available entry),
  // but kept to satisfy the type checker for the `!visible` early return below.
  const activeDisplayName = useMemo(() => {
    if (!userProfile) {
      return null;
    }
    const activeId = contextId ?? userProfile.account.id;
    return availableContexts.find((ctx) => ctx.id === activeId)?.displayName ?? null;
  }, [availableContexts, contextId, userProfile]);

  if (!visible || !activeDisplayName) {
    return null;
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label="Active account context"
      sx={{
        height: `${ACCOUNT_CONTEXT_BANNER_HEIGHT}px`,
        px: { xs: 2, md: 5 },
        display: "flex",
        alignItems: "center",
        bgcolor: (theme: Theme) => theme.palette.grey[100],
        borderTop: (theme: Theme) => `1px solid ${theme.palette.divider}`,
        // Sit above page content but below modals/drawers. NavBar AppBar defaults
        // to zIndex.appBar (1100); keep the banner just under that so fixed nav
        // elements (menus, tooltips) remain on top.
        zIndex: (theme: Theme) => theme.zIndex.appBar - 1,
      }}
    >
      <Typography component="span" variant="body2" sx={{ fontSize: "0.875rem", color: "text.secondary", mr: 0.75 }}>
        Viewing:
      </Typography>
      <Typography
        component="span"
        variant="body2"
        sx={{ fontSize: "0.875rem", fontWeight: 600, color: "text.primary" }}
        noWrap
      >
        {activeDisplayName}
      </Typography>
    </Box>
  );
};

export default AccountContextBanner;
